import { BankOperationStatusesNew } from 'constants/bankOperationStatusesNew';

import { useMemo, useState } from 'react';

import { getTime, startOfDay } from 'date-fns';
import omit from 'lodash/omit';
import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import {
  DateRangeTimezoneValue,
  DateRangeValue,
} from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { isDateRangeApplied } from 'shared/mui/DatePicker/utils/isDateRangeApplied';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { BankOperationsFilterChips } from './BankOperationsFilterChips';
import { BankOperationsFilterForm } from './BankOperationsFilterForm';

type BankOperationsFilterValues = {
  appliedDate: DateRangeValue;
  canceledDate: DateRangeValue;
  approveDate: DateRangeValue;
  timezone: DateRangeTimezoneValue;
  merchant: string[];
  provider: string[];
  merchantAccount: string[];
  type: string[];
  direction: string;
  status: string[];
  referral: string[];
  user: string;
};

const DEFAULT_INTERVAL: BankOperationsFilterValues['appliedDate'] = [
  getTime(startOfDay(new Date())),
  Date.now(),
];

export const RESET_VALUES = {
  appliedDate: [null, null] as DateRangeValue,
  canceledDate: [null, null] as DateRangeValue,
  approveDate: [null, null] as DateRangeValue,
  timezone: null,
  merchant: [],
  provider: [],
  merchantAccount: [],
  referral: [],
  type: [],
  status: [],
  direction: '',
  user: '',
};

export const BankOperationsFilter: React.FC = () => {
  const { filterValues } = useListContext();

  // Не восстанавливать дефолтные фильтры после ручной очистки
  const [disableDefaultFilters, setDisableDefaultFilters] = useState(false);

  const computedDefaultValues = useMemo<BankOperationsFilterValues>(() => {
    const isEmpty = Object.keys(cleanEmpty(filterValues)).length === 0;
    const shouldApplyDefaults = isEmpty && !disableDefaultFilters;

    return {
      appliedDate: shouldApplyDefaults
        ? DEFAULT_INTERVAL
        : [
            formatInputToTimestamp(filterValues['dateOfOperation[gte]']),
            formatInputToTimestamp(filterValues['dateOfOperation[lte]']),
          ],
      canceledDate: [
        formatInputToTimestamp(filterValues?.['canceledAt[gte]']),
        formatInputToTimestamp(filterValues?.['canceledAt[lte]']),
      ],
      approveDate: [
        formatInputToTimestamp(filterValues?.['approveAt[gte]']),
        formatInputToTimestamp(filterValues?.['approveAt[lte]']),
      ],
      timezone: isEmpty ? DEFAULT_TIMEZONE : filterValues.timezone || null,
      merchant: filterValues.merchant || [],
      provider: filterValues.provider || [],
      merchantAccount: filterValues.merchantAccount || [],
      type: filterValues.type || [],
      status:
        filterValues.status || (shouldApplyDefaults ? [BankOperationStatusesNew.Approved] : []),
      referral: filterValues.referral || [],
      direction: filterValues.direction || '',
      user: filterValues.user || '',
    };
  }, [JSON.stringify(filterValues), disableDefaultFilters]);

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<BankOperationsFilterValues>({
      defaultValues: computedDefaultValues,
      resetValues: RESET_VALUES,
    });

  useSyncFilter<BankOperationsFilterValues>({
    appliedFilters,
    transform: (appliedFilters) => {
      const {
        appliedDate = [null, null],
        approveDate = [null, null],
        canceledDate = [null, null],
        timezone,
        ...restFilters
      } = appliedFilters;
      return cleanEmpty({
        ...restFilters,
        'dateOfOperation[gte]': formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
        'dateOfOperation[lte]': formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        'approveAt[gte]': formatInputToTimestamp(approveDate?.[0], { unit: 'seconds' }),
        'approveAt[lte]': formatInputToTimestamp(approveDate?.[1], { unit: 'seconds' }),
        'canceledAt[gte]': formatInputToTimestamp(canceledDate?.[0], { unit: 'seconds' }),
        'canceledAt[lte]': formatInputToTimestamp(canceledDate?.[1], { unit: 'seconds' }),
        timezone: getAppliedTimezone(timezone, appliedDate, approveDate, canceledDate),
      });
    },
  });

  const handleReset = () => {
    setDisableDefaultFilters(true);
    onReset();
  };

  const { data } = useListContext();

  const isListFilterExist = useMemo(() => {
    const { appliedDate, canceledDate, approveDate, ...restFilters } = appliedFilters;

    return (
      Object.keys(omit(cleanEmpty(restFilters), 'timezone')).length > 0 ||
      isDateRangeApplied(appliedDate) ||
      isDateRangeApplied(canceledDate) ||
      isDateRangeApplied(approveDate)
    );
  }, [appliedFilters]);

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton disabled={!data?.length} key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <BankOperationsFilterForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={handleReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <BankOperationsFilterChips />
            <FilterClearButton key="clear" onClick={handleReset} visible={isListFilterExist} />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
